/**
 * Supabase-backed source of truth for the signed-in user's
 * `circle_members` "self" row.
 *
 * Wraps the `public.get_or_create_self_member(p_display_name,
 * p_region)` RPC (idempotent — returns the existing self-row if one
 * exists, bootstraps household + self-row if not) and the
 * `circle_members.consent_*` columns that back the consent UI.
 *
 * This is the missing identity + consent layer for the lbx-training
 * SDK pathway (`training-capture-coordinator.ts` + `consent-store.ts`).
 * Before this module, identity came from manual localStorage overrides
 * and consent lived in localStorage with no cross-device propagation.
 *
 * Mirrors the iOS / Android `SelfMemberService` equivalents.
 */

import { createClient } from '@/lib/supabase/client'
import { createLogger } from '@/lib/monitoring/logger'

const log = createLogger('self-member-service')

export interface SelfMemberIdentity {
  householdId: string
  memberId: string
}

export interface SelfMemberConsent {
  audio: boolean
  camera: boolean
  training: boolean
  version: string | null
  bipaAck: boolean
}

export interface ConsentGrantsPatch {
  audio?: boolean
  camera?: boolean
  training?: boolean
  bipaAck?: boolean
  version?: string
}

const DEFAULT_CONSENT: SelfMemberConsent = {
  audio: false,
  camera: false,
  training: false,
  version: null,
  bipaAck: false,
}

/**
 * Best-effort display-name derivation from the auth user.
 *
 * Order: `user_metadata.full_name` → `user_metadata.name` →
 * `email` (local part) → "Member". The RPC accepts any non-empty
 * string and uses it as the seed display_name only when bootstrapping.
 */
function deriveDisplayName(user: {
  email?: string | null
  user_metadata?: Record<string, unknown> | null
}): string {
  const meta = user.user_metadata ?? {}
  const candidates = [meta.full_name, meta.name, meta.display_name]
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim()) return c.trim()
  }
  const email = user.email
  if (email && email.includes('@')) {
    const local = email.split('@')[0]
    if (local) return local
  }
  return 'Member'
}

/**
 * Best-effort region tag for the RPC's `p_region` parameter.
 *
 * Used only when bootstrapping — once the row exists the RPC ignores
 * the seed. Falls back to "US" when `navigator.language` lacks a region
 * suffix so the RPC always sees a non-null value.
 */
function deriveRegion(): string {
  if (typeof navigator === 'undefined') return 'US'
  const lang = navigator.language || ''
  const region = lang.split('-')[1]
  if (region && region.length >= 2) return region.toUpperCase()
  return 'US'
}

/**
 * Idempotent identity bootstrap. Returns the user's
 * `(household_id, member_id)` self pair, creating the household +
 * self row on first call if absent.
 *
 * Throws when the user is not signed in or the RPC fails — callers
 * (the layout sign-in hook, the consent UI) treat throws as
 * "identity unavailable" and degrade gracefully.
 */
export async function fetchOrCreateSelfMember(): Promise<SelfMemberIdentity> {
  const supabase = createClient()
  const { data: userData, error: userErr } = await supabase.auth.getUser()
  if (userErr || !userData?.user) {
    throw new Error('not signed in')
  }
  const displayName = deriveDisplayName(userData.user)
  const region = deriveRegion()

  const { data, error } = await supabase.rpc('get_or_create_self_member', {
    p_display_name: displayName,
    p_region: region,
  })
  if (error) {
    log.warn('get_or_create_self_member RPC failed', {
      code: error.code,
      message: error.message,
    })
    throw new Error(`self-member rpc failed: ${error.message}`)
  }

  // The RPC returns `TABLE (household_id uuid, member_id uuid)` —
  // PostgREST surfaces this as an array of one row.
  const row = Array.isArray(data) ? data[0] : data
  const householdId = row?.household_id as string | undefined
  const memberId = row?.member_id as string | undefined
  if (!householdId || !memberId) {
    throw new Error('self-member rpc returned empty row')
  }
  return { householdId, memberId }
}

/**
 * Read the current consent grants from the user's `circle_members`
 * row. Returns `DEFAULT_CONSENT` if the row is unreadable (RLS,
 * network) so callers always have a safe-closed baseline.
 */
export async function readSelfMemberConsent(
  memberId: string,
): Promise<SelfMemberConsent> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('circle_members')
    .select('consent_audio, consent_camera, consent_training, consent_version, bipa_ack')
    .eq('id', memberId)
    .maybeSingle()
  if (error) {
    log.warn('readSelfMemberConsent failed', {
      code: error.code,
      message: error.message,
      memberId,
    })
    return { ...DEFAULT_CONSENT }
  }
  if (!data) return { ...DEFAULT_CONSENT }
  return {
    audio: !!data.consent_audio,
    camera: !!data.consent_camera,
    training: !!data.consent_training,
    version: typeof data.consent_version === 'string' ? data.consent_version : null,
    bipaAck: !!data.bipa_ack,
  }
}

/**
 * Write a partial consent patch back to the user's `circle_members`
 * row. Stamps `consent_signed_at = now()` whenever any consent flag
 * is included in the patch so the audit trail captures every flip.
 *
 * RLS allows authenticated users to UPDATE their own self-row only.
 */
export async function updateSelfMemberConsent(
  memberId: string,
  patch: ConsentGrantsPatch,
): Promise<void> {
  const supabase = createClient()
  const update: Record<string, unknown> = {}
  if (typeof patch.audio === 'boolean') update.consent_audio = patch.audio
  if (typeof patch.camera === 'boolean') update.consent_camera = patch.camera
  if (typeof patch.training === 'boolean') update.consent_training = patch.training
  if (typeof patch.bipaAck === 'boolean') update.bipa_ack = patch.bipaAck
  if (typeof patch.version === 'string') update.consent_version = patch.version
  if (Object.keys(update).length === 0) return

  // Any flag flip refreshes the BIPA-equivalent "consent signed at"
  // audit timestamp. The version is also stamped so we can detect
  // pre-version-rev grants during downstream consent refreshes.
  update.consent_signed_at = new Date().toISOString()

  const { error } = await supabase
    .from('circle_members')
    .update(update)
    .eq('id', memberId)
  if (error) {
    log.warn('updateSelfMemberConsent failed', {
      code: error.code,
      message: error.message,
      memberId,
    })
    throw new Error(`update consent failed: ${error.message}`)
  }
}
